/**
 * product-price.js
 *
 * Fake Prices logic for product cards (collections, Recently Viewed, etc.).
 *
 * How it works:
 * 1. Runs once on DOMContentLoaded for cards already in the DOM.
 * 2. Re-runs scoped to the <recently-viewed> element after it fires
 *    the "recentlyViewed:loaded" event (dynamic async injection).
 * 3. Re-runs scoped to each [role="tabpanel"] container (featured-collections
 *    section) when content is injected by APIButton via Section Rendering API.
 *    Non-first tabs are NOT rendered in the initial SSR HTML — they are
 *    fetched and injected after the page loads, so DOMContentLoaded's initial
 *    pass never sees their cards. watchTabPanels() catches the replaceWith()
 *    DOM mutation and processes each panel as soon as its cards arrive.
 * 4. For each product card that matched a discount group (Liquid rendered
 *    #first-price / #final-price-* inside the card), reads the base price
 *    from #first-price .fake-compare-price .money, applies the discount, and
 *    writes the result into the INNERMOST .money inside #final-price-* so that
 *    the outer wrapper span structure (added by shop currency formatters) is
 *    preserved.
 * 5. Discount values (amount / percentage) are read from the hidden
 *    .placeholder-for-cmc block rendered by product-price.liquid.
 *    The lookup first searches within `container`, then falls back to
 *    `document` so Recently Viewed cards still find the values even when
 *    they are the only cards on the page.
 * 6. Formats any JS-updated amounts through theme.Currency.formatMoney so
 *    Shopify Markets / active presentment currency display stays consistent.
 *
 * Loaded on every page when settings.show-fake-compare-price == true.
 * (Safe on non-collection pages: querySelector returns null, no-ops run.)
 */

var fakePriceCardCodeExecuted = false;

function parseMoneyCards(text) {
  return parseFloat((text || '').replace(/[^\d.,]/g, '').replace(',', '.')) || 0;
}

function calcDiscountedCards(base, amount, type) {
  if (type === 'percentage') return Math.round(base * (1 - amount / 100));
  return Math.round(base - amount);
}

function formatFakeMoneyCards(amount) {
  return theme.Currency.formatMoneyText(Math.round(amount * 100), theme.settings.moneyFormat);
}

/**
 * Return the innermost .money element inside `container`.
 *
 * Some Shopify stores configure a currency format like
 *   <span class="money">{{amount}}</span>
 * which causes the Liquid output to be double-nested:
 *   <span class="money">               ← outer wrapper
 *     <span class="money">$159</span>  ← inner text carrier
 *   </span>
 *
 * We must update ONLY the innermost span (the one with no .money children)
 * so the wrapper structure is preserved for CSS and currency converters.
 */
function getInnermostMoneyEl(container) {
  const all = Array.from(container.querySelectorAll('.money'));
  // Prefer the deepest element that has no .money children.
  return all.find(el => !el.querySelector('.money')) || all[0] || null;
}

/**
 * Apply fake prices to all product cards inside `container`.
 *
 * Card-type-agnostic: instead of querying for a specific wrapper class
 * (.product-card, .horizontal-product, etc.) we locate every .fake-price-wrap
 * element — the shared anchor rendered by product-price.liquid for any card
 * type.  This means the function works equally for:
 *   • .product-card    — collection grids, featured sections, recently-viewed
 *   • .horizontal-product — cart drawer recently-viewed tab
 *   • any future card layout that uses product-price.liquid
 *
 * @param {Element|Document} [container] - Scope for queries. Defaults to
 *   `document` (initial page load). Pass a narrower element (e.g. a tabpanel
 *   or a <recently-viewed>) to process only dynamically injected cards.
 */
function applyFakePricesOnCards(container) {
  const isGlobalRun = !container || container === document;

  if (isGlobalRun && fakePriceCardCodeExecuted) return;

  const root = isGlobalRun ? document : container;

  // Discount values come from .placeholder-for-cmc rendered by product-price.liquid
  // inside every card.  All cards share the same theme-settings values so the
  // first matching element in `root` (or the whole document as fallback) is fine.
  const getDiscount = id => parseFloat(
    (root.querySelector('#' + id) || document.querySelector('#' + id))
      ?.textContent || 0
  );

  const discounts = {
    'final-price':                         { amount: getDiscount('discount-amount'),           type: 'amount' },
    'final-price-two':                     { amount: getDiscount('discount-amount-two'),        type: 'amount' },
    'final-price-precents-desktop':        { amount: getDiscount('discount-percents'),          type: 'percentage' },
    'final-price-precents-desktop-two':    { amount: getDiscount('discount-percents-two'),      type: 'percentage' },
    'final-price-precents-desktop-three':  { amount: getDiscount('discount-percents-three'),    type: 'percentage' },
    'final-price-precents-desktop-fourth': { amount: getDiscount('discount-percents-fourth'),   type: 'percentage' },
    'final-price-precents-desktop-fifth':  { amount: getDiscount('discount-percents-fifth'),    type: 'percentage' },
    'final-price-precents-desktop-sixth':  { amount: getDiscount('discount-percents-sixth'),    type: 'percentage' },
  };

  // Iterate over every fake-price wrapper in `root`.  Each .fake-price-wrap
  // corresponds to exactly one product card's price block and contains both
  // the compare-price span and all the possible final-price spans, so we can
  // scope the update to the wrapper without touching other cards on the page.
  root.querySelectorAll('.fake-price-wrap').forEach(fpWrap => {
    const compareWrap = fpWrap.querySelector('.fake-compare-price');
    if (!compareWrap) return;
    const basePrice = parseMoneyCards(compareWrap.textContent);
    if (!basePrice) return;

    Object.entries(discounts).forEach(([id, cfg]) => {
      if (!cfg.amount) return;

      // Search for the final-price span WITHIN this wrapper only.
      const finalWrap = fpWrap.querySelector('#' + id);
      if (!finalWrap) return;

      // Target the innermost .money element to preserve any outer wrapper
      // spans injected by the shop's currency formatter.
      const moneyEl = getInnermostMoneyEl(finalWrap);
      if (!moneyEl) return;

      moneyEl.textContent = formatFakeMoneyCards(calcDiscountedCards(basePrice, cfg.amount, cfg.type));
    });
  });

  if (isGlobalRun) {
    fakePriceCardCodeExecuted = true;
  }

}

/**
 * Watch [role="tabpanel"] containers for cards dynamically injected by the
 * Section Rendering API (APIButton / api-button custom element).
 *
 * In the "featured-collections" section, only the FIRST tab's product cards
 * are rendered in the initial SSR HTML.  Every other tab has an empty
 * <div id="TabPanelContent-…"> that is later replaced via:
 *
 *   document.getElementById(id).replaceWith(parsedHTML.getElementById(id));
 *
 * This replaceWith() removes the empty child and inserts the card-filled
 * element.  From the stable parent ([role="tabpanel"]) point of view that is
 * a childList mutation — exactly what our MutationObserver watches for.
 *
 * Why this works in the theme editor but not on the live site:
 *   The Shopify theme editor re-renders the full section HTML every time a
 *   setting changes, so all tabs' cards land in the DOM before any script
 *   runs.  On the live site the SSR pass skips non-first tabs and APIButton
 *   injects them asynchronously — after DOMContentLoaded has already run.
 */
function watchTabPanels() {
  document.querySelectorAll('[role="tabpanel"]').forEach(panel => {
    // Panel already has fake-price cards (first tab rendered by SSR, or
    // content already injected before DOMContentLoaded).  The global
    // applyFakePricesOnCards() call handles these — no observer needed.
    if (panel.querySelector('.fake-price-wrap')) return;

    const obs = new MutationObserver(() => {
      // Wait until fake-price cards actually land in the panel.
      if (!panel.querySelector('.fake-price-wrap')) return;

      // Process ONLY this panel so we don't re-scan the whole document.
      applyFakePricesOnCards(panel);
      obs.disconnect();
    });

    // childList on the panel catches the replaceWith() that swaps the empty
    // #TabPanelContent-… div for the card-filled one.
    obs.observe(panel, { childList: true });
  });
}

/**
 * Watch for <recently-viewed> cards being injected into the DOM.
 *
 * We use MutationObserver (not just the recentlyViewed:loaded event) because:
 *  - Both product-price.js and theme.js are loaded with `defer`.
 *  - theme.js registers the custom element, whose constructor immediately
 *    sets up a Motion.inView (IntersectionObserver) with a 600 px margin.
 *  - On fast/cached loads the IntersectionObserver can fire and the search
 *    fetch can resolve before DOMContentLoaded runs our event listener setup.
 *  - MutationObserver runs as a microtask right after the innerHTML assignment
 *    in RecentlyViewed.init(), so it is immune to that race condition.
 *
 * The recentlyViewed:loaded event listener is kept as an extra safety net.
 * A shared `handled` flag prevents the function from running twice if both
 * the MutationObserver and the event fire in quick succession.
 */
function watchRecentlyViewed(recentlyViewedEl) {
  let handled = false;

  function handle() {
    if (handled) return;
    handled = true;
    mutObs.disconnect();
    applyFakePricesOnCards(recentlyViewedEl);
  }

  // Primary: MutationObserver fires as a microtask right after innerHTML is
  // set, regardless of whether the event listener was already attached.
  const mutObs = new MutationObserver(handle);
  mutObs.observe(recentlyViewedEl, { childList: true });

  // Secondary: recentlyViewed:loaded event dispatched by theme.js after
  // innerHTML. Covers edge cases where the MutationObserver and the event
  // fire in different microtask cycles.
  recentlyViewedEl.addEventListener('recentlyViewed:loaded', handle);
}

document.addEventListener('DOMContentLoaded', () => {
  // Initial pass: process all product cards already present in the DOM.
  applyFakePricesOnCards();

  // Future <recently-viewed> elements can be injected by another async section
  // before they load their own cards, so catch their loaded event at document
  // level as well as wiring the elements that already exist now.
  document.addEventListener('recentlyViewed:loaded', event => {
    if (event.target?.matches?.('recently-viewed')) {
      applyFakePricesOnCards(event.target);
    }
  });

  // Wire up watchers for ALL <recently-viewed> elements on the page.
  // We use querySelectorAll (not querySelector) because the cart drawer
  // also contains a <recently-viewed> element — a plain querySelector
  // returns the cart-drawer one first and never reaches the content section.
  document.querySelectorAll('recently-viewed').forEach(recentlyViewedEl => {
    // Use .fake-price-wrap instead of .product-card so both card layouts
    // (regular cards and horizontal cart-drawer cards) are detected.
    if (recentlyViewedEl.querySelector('.fake-price-wrap')) {
      applyFakePricesOnCards(recentlyViewedEl);
    } else {
      watchRecentlyViewed(recentlyViewedEl);
    }
  });

  // Watch tab panels for product cards injected by the Section Rendering API
  // (featured-collections section — non-first tabs).
  watchTabPanels();
});
